import { useState, useEffect } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Print header showing the user's Business Info (Company / DBA / Address / Phone).
 * - Hidden on screen by default (only appears in print preview / print output).
 * - Pass `alwaysVisible={true}` to also show on screen (e.g., screen-friendly tools).
 * - Pass `inverse={true}` for white-on-dark text (looks good on green schedule banner).
 *
 * Configured once from /api/user/profile (cached in memory via React state).
 */
export default function PrintHeader({ alwaysVisible = false, inverse = false }) {
  const [profile, setProfile] = useState(null);
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/user/profile`).then(res => {
      if (!cancelled) setProfile(res.data);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  if (!profile) return null;
  const company = profile.company_name || profile.store_name || "";
  const dba = profile.dba_name || "";
  const address = profile.business_address || profile.store_address || "";
  const phone = profile.phone_number || "";
  const ein = profile.ein || "";
  if (!company && !dba && !address && !phone && !ein) return null;

  const wrapperClasses = `print-header ${alwaysVisible ? "" : "print-only"} ${inverse ? "print-header--inverse" : ""}`;

  return (
    <div className={wrapperClasses} data-testid="print-header">
      <div className="ph-company">{company || dba}</div>
      {dba && company && dba !== company && <div className="ph-dba">DBA: {dba}</div>}
      {address && <div className="ph-line">{address}</div>}
      {(phone || ein) && (
        <div className="ph-line">
          {phone && <span>Tel: {phone}</span>}
          {phone && ein && <span>  •  </span>}
          {ein && <span>EIN: {ein}</span>}
        </div>
      )}
      <style>{`
        .print-header {
          font-family: 'Courier New', monospace;
          color: #000;
          text-align: center;
          padding: 6px 8px 8px;
          border-bottom: 1.5px solid #000;
          margin-bottom: 6px;
        }
        .print-header.print-only { display: none; }
        .print-header--inverse {
          background: transparent;
          color: #fff;
          border-bottom: 1px solid rgba(255,255,255,0.4);
        }
        .ph-company {
          font-weight: 800;
          font-size: 16px;
          letter-spacing: 0.5px;
          line-height: 1.1;
        }
        .ph-dba {
          font-size: 11px;
          font-style: italic;
          line-height: 1.2;
          margin-top: 1px;
        }
        .ph-line {
          font-size: 11px;
          line-height: 1.25;
          margin-top: 1px;
        }
        @media print {
          .print-header.print-only { display: block; }
        }
      `}</style>
    </div>
  );
}
